// ANETT HORVATH — Editable Content Module
// No-code content management. Admin edits text without deploy.

import type { SupportedLang } from '../config/house.config';

export interface EditableContent {
  id: string;
  content_key: string;
  translations: Record<string, { text: string }>;
  created_at: string;
  updated_at: string;
}

const CONTENT_KEY = 'ah_editable_content';

function loadContent(): EditableContent[] {
  try {
    const raw = localStorage.getItem(CONTENT_KEY);
    return raw ? JSON.parse(raw) : seedContent();
  } catch { return seedContent(); }
}

function saveContent(items: EditableContent[]) {
  localStorage.setItem(CONTENT_KEY, JSON.stringify(items));
}

function seedContent(): EditableContent[] {
  const ts = new Date().toISOString();
  const items: EditableContent[] = [
    { id: crypto.randomUUID(), content_key: 'inner_circle_message', translations: { en: { text: 'Welcome to the Inner Circle. Your journey begins here.' }, hu: { text: 'Üdvözöljük az Inner Circle-ben. Útja itt kezdődik.' }, de: { text: 'Willkommen im Inner Circle. Ihre Reise beginnt hier.' }, it: { text: 'Benvenuto nell\'Inner Circle. Il tuo viaggio inizia qui.' }, fr: { text: 'Bienvenue dans l\'Inner Circle. Votre voyage commence ici.' } }, created_at: ts, updated_at: ts },
    { id: crypto.randomUUID(), content_key: 'waiting_phase_day2', translations: { en: { text: 'The House is preparing. Your patience reflects your understanding.' }, hu: { text: 'A Ház készülődik. Türelme megértését tükrözi.' }, de: { text: 'Das Haus bereitet sich vor. Ihre Geduld spiegelt Ihr Verständnis.' }, it: { text: 'La Casa si prepara. La tua pazienza riflette la tua comprensione.' }, fr: { text: 'La Maison se prépare. Votre patience reflète votre compréhension.' } }, created_at: ts, updated_at: ts },
    { id: crypto.randomUUID(), content_key: 'allocation_intro', translations: { en: { text: 'Allocation is open. Select with intention.' }, hu: { text: 'Az allokáció nyitva. Válasszon szándékkal.' }, de: { text: 'Die Zuteilung ist offen. Wählen Sie mit Absicht.' }, it: { text: 'L\'allocazione è aperta. Scegli con intenzione.' }, fr: { text: 'L\'allocation est ouverte. Choisissez avec intention.' } }, created_at: ts, updated_at: ts },
    { id: crypto.randomUUID(), content_key: 'arrival_message', translations: { en: { text: 'Your selection has arrived. Handle with reverence.' }, hu: { text: 'Választása megérkezett. Kezelje tisztelettel.' }, de: { text: 'Ihre Auswahl ist angekommen. Behandeln Sie sie mit Ehrfurcht.' }, it: { text: 'La tua selezione è arrivata. Trattala con riverenza.' }, fr: { text: 'Votre sélection est arrivée. Manipulez avec révérence.' } }, created_at: ts, updated_at: ts },
  ];
  saveContent(items);
  return items;
}

export function getAllContent(): EditableContent[] {
  return loadContent();
}

export function getContent(key: string, lang: SupportedLang): string {
  const items = loadContent();
  const item = items.find(c => c.content_key === key);
  if (!item) return '';
  return item.translations[lang]?.text || item.translations.en?.text || '';
}

export function upsertContent(id: string | null, key: string, translations: Record<string, { text: string }>): EditableContent {
  const items = loadContent();
  const ts = new Date().toISOString();
  if (id) {
    const idx = items.findIndex(c => c.id === id);
    if (idx >= 0) {
      items[idx] = { ...items[idx], content_key: key, translations, updated_at: ts };
      saveContent(items);
      return items[idx];
    }
  }
  const newItem: EditableContent = { id: crypto.randomUUID(), content_key: key, translations, created_at: ts, updated_at: ts };
  items.push(newItem);
  saveContent(items);
  return newItem;
}

export function deleteContent(id: string) {
  const items = loadContent().filter(c => c.id !== id);
  saveContent(items);
}
